/** @jsx isolateComponent */
import { Fragment, isValidElement } from 'react';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import {
  BrandButton,
  Button,
  ButtonProps,
  Colors,
  DarkBackgroundButton,
  Heading,
  SubscriptionFormWithDisclaimer,
  Text,
  Typography,
  Icon,
  TagLabel,
  Grid,
} from '../../../../index';
import {
  getSupportedCTAColor,
  mapBackgroundColorToV5HeadingColorName,
  mapBackgroundColorToV5TextColorName,
  transformBackgroundColor,
  validateZapierBackgroundAccessibility,
} from '../utils';
import { breakpoints } from '../constants';
import { CTAColor, HeroColorsV4, HeroColorsV5 } from '../types';
import type { AnchorTarget } from '../../../../ts-types';
import type { IconName } from '../../../display/Icon';

export type Props = {
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white' | 'neutral100' | 'zapier' | 'night' | 'moss' | 'neutral800'`.
   *
   * It also controls the color of the CTA button - background color
   * is white, then the CTA button is `primary`. Otherwise, it is `primary-alt`
   * */
  backgroundColor?: HeroColorsV4 | HeroColorsV5;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  description?: React.ReactNode;
  /**
   * Whether the subscription form is enabled in place of CTA button.
   */
  isSubscriptionForm?: boolean;
  /**
   * Placeholder text to display inside the subscription form input field.
   */
  subscriptionPlaceholder?: string;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * Background color of the CTA button.
   */
  ctaColor?: CTAColor;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Text displayed next to CTA button.
   */
  ctaDisclaimer?: React.ReactNode;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component. */
  children?: React.ReactNode;
  /**
   * Function that gets called on subscription form submit.
   */
  onSubscriptionFormSubmit?: (subscriptionValue?: string) => void;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   */
  labelIcon?: IconName;
  /**
   * The labelColor determines the background-color of the label.
   */
  labelColor?:
    | 'info'
    | 'success'
    | 'error'
    | 'warning'
    | 'white'
    | 'gray'
    | 'peach'
    | 'neutral300'
    | 'bolt'
    | 'cream';
};

const SubscriptionFormStyles = {
  ctaContainer: css`
    @media screen and (min-width: ${breakpoints.tablet}px) {
      align-items: stretch;
      flex-direction: column;
    }

    // Button shouldn't take up full width when subscription form is enabled;
    button,
    a {
      width: auto;
    }
  `,
  ctaDisclaimer: css`
    margin: 10px 0;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin: 10px 0;
    }
    @media screen and (max-width: ${breakpoints.mobile}px) {
      text-align: left;
    }
  `,
};

const Styles = labelStyles('Hero', {
  root: (backgroundColor: Props['backgroundColor'] = 'neutral100') => css`
    background-color: ${Colors[backgroundColor]};
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 40px;
    }
  `,
  content: (maxWidth: number) => css`
    display: grid;
    align-items: center;
    grid-template-columns: repeat(1, 1fr);
    gap: 40px;
    max-width: ${maxWidth}px;
    margin: 0 auto;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      grid-template-columns: repeat(2, 1fr);
    }
  `,
  labelContainer: css`
    margin-bottom: 10px;
  `,
  heading: (backgroundColor: Props['backgroundColor'] = 'neutral100') => css`
    ${Typography.pageHeader4}
    color: ${Colors[mapBackgroundColorToV5TextColorName(backgroundColor)]};
    margin-bottom: 10px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.pageHeader3}
      margin-bottom: 20px;
    }
  `,
  description: (
    backgroundColor: Props['backgroundColor'] = 'neutral100'
  ) => css`
    ${Typography.pageHeader9}
    color: ${Colors[mapBackgroundColorToV5HeadingColorName(backgroundColor)]};
  `,
  textContent: css`
    max-width: 470px;
    text-align: center;
    margin: 0 auto;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin: 0;
      text-align: left;
    }
  `,
  mediaContent: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  ctaContainer: (
    isSubscriptionForm: Props['isSubscriptionForm'] = false
  ) => css`
    margin-top: 20px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 40px;
      display: flex;
      align-items: center;
      text-align: left;
    }
    ${isSubscriptionForm &&
    withModifier('subscription-form', SubscriptionFormStyles.ctaContainer)}
  `,
  ctaDisclaimer: (
    isSubscriptionForm: Props['isSubscriptionForm'] = false
  ) => css`
    margin: 10px auto 0 auto;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 0;
      margin-left: 20px;
    }

    ${isSubscriptionForm &&
    withModifier(
      'subscription-form-disclaimer',
      SubscriptionFormStyles.ctaDisclaimer
    )}
  `,
});

export const HeroStandard = ({
  backgroundColor = 'neutral100',
  ctaColor = 'primary',
  isSubscriptionForm = false,
  subscriptionPlaceholder = 'Email address',
  heading,
  description,
  ctaText,
  ctaHref,
  ctaDisclaimer,
  onCtaClick,
  onCtaKeyDown,
  onSubscriptionFormSubmit,
  children,
  maxWidth = 1170,
  ctaTarget,
  label,
  labelIcon,
  labelColor,
}: Props) => {
  const mappedBackgroundColor = transformBackgroundColor(backgroundColor);

  // This should validate and through a console error as the disclaimer text accessibility
  // is poor on the zapier background.
  validateZapierBackgroundAccessibility(
    mappedBackgroundColor,
    isSubscriptionForm,
    !!ctaDisclaimer
  );

  const getCtaComponent = () => {
    const sharedProps = {
      children: ctaText,
      href: !isSubscriptionForm ? ctaHref : undefined,
      onClick: onCtaClick,
      onKeyDown: onCtaKeyDown,
      isFullWidth: true,
      type: isSubscriptionForm ? 'submit' : ('button' as ButtonProps['type']),
      target: ctaTarget,
    };

    const color = getSupportedCTAColor(ctaColor, mappedBackgroundColor);

    return color === 'brand' ? (
      <BrandButton {...sharedProps} />
    ) : color === 'primary-dark' ? (
      <DarkBackgroundButton {...sharedProps} />
    ) : (
      // All the button sizes need to be size small so they are 44px in V5.
      // Also, Button and Input both need to be consistent and size small when we use them side by side
      // like in a subscription form. (This should also applicable to V4)
      <Button {...sharedProps} color={color} size="small" />
    );
  };

  const isDisclaimerElement = !!ctaDisclaimer && isValidElement(ctaDisclaimer);

  return (
    <div css={Styles.root(mappedBackgroundColor)}>
      <div css={Styles.content(maxWidth)}>
        <div css={Styles.textContent}>
          {label && (
            <div css={Styles.labelContainer}>
              <TagLabel color={labelColor}>
                {labelIcon ? (
                  <Grid alignItems="center" columns="auto max-content" gap={5}>
                    <Icon name={labelIcon} size={20} />
                    {label}
                  </Grid>
                ) : (
                  label
                )}
              </TagLabel>
            </div>
          )}
          <Heading css={Styles.heading(mappedBackgroundColor)}>
            {heading}
          </Heading>
          {description && (
            <div css={Styles.description(mappedBackgroundColor)}>
              {description}
            </div>
          )}
          {ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && (
            <div css={Styles.ctaContainer(isSubscriptionForm)}>
              {isSubscriptionForm ? (
                <SubscriptionFormWithDisclaimer
                  placeholderText={subscriptionPlaceholder}
                  onSubmit={onSubscriptionFormSubmit}
                  renderButton={() => getCtaComponent()}
                  disclaimerTextColor={
                    mappedBackgroundColor === 'neutral100'
                      ? 'primary'
                      : 'secondary'
                  }
                />
              ) : (
                <Fragment>
                  <div>{getCtaComponent()}</div>
                  {ctaDisclaimer && (
                    <div css={Styles.ctaDisclaimer(isSubscriptionForm)}>
                      {isDisclaimerElement ? (
                        ctaDisclaimer
                      ) : (
                        <Text
                          tag="p"
                          type="paragraph3"
                          color={mapBackgroundColorToV5HeadingColorName(
                            mappedBackgroundColor
                          )}
                        >
                          {ctaDisclaimer}
                        </Text>
                      )}
                    </div>
                  )}
                </Fragment>
              )}
            </div>
          )}
        </div>
        <div css={Styles.mediaContent}>{children}</div>
      </div>
    </div>
  );
};
